/**
 * Conversations hook for Clean UI (no-login mode).
 *
 * Uses agentApi to communicate directly with the agent for conversation
 * management. Removed agent selection (single-agent mode).
 *
 * Blueprint: specs/019-clean-ui-no-api/
 */

import { useState, useCallback, useRef } from 'react';
import {
  agentApi,
  type AgentConversationSummary,
  type AgentConversationDetail,
  type AgentMessage,
} from '../services/agentApi';

// Re-export types for backwards compatibility with existing components
export interface Conversation {
  id: string;
  session_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
}

// Convert agent API types to app types
function mapConversation(conv: AgentConversationSummary): Conversation {
  return {
    id: conv.id,
    session_id: conv.id, // In clean UI, conversation ID = session ID
    title: conv.title,
    created_at: conv.created_at,
    updated_at: conv.updated_at,
  };
}

function mapMessage(msg: AgentMessage): Message {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    timestamp: msg.timestamp,
  };
}

function mapConversationDetail(
  detail: AgentConversationDetail,
): ConversationDetail {
  return {
    ...mapConversation(detail),
    messages: (detail.messages || []).map(mapMessage),
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useConversations(userId: number | string | null) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] =
    useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Ignore slow responses when the user navigates to another conversation. */
  const latestRequestedIdRef = useRef<string | null>(null);

  const fetchConversations = useCallback(async (): Promise<Conversation[]> => {
    setLoading(true);
    setError(null);

    try {
      const data = await agentApi.listConversations();
      const mapped = data.map(mapConversation);
      setConversations(mapped);
      return mapped;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch conversations',
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      latestRequestedIdRef.current = conversationId;
      setLoading(true);
      setError(null);

      try {
        const data = await agentApi.getConversation(conversationId);
        if (latestRequestedIdRef.current !== conversationId) {
          return true;
        }
        setActiveConversation(mapConversationDetail(data));
        return true;
      } catch (err) {
        if (latestRequestedIdRef.current !== conversationId) {
          return true;
        }
        setError(
          err instanceof Error ? err.message : 'Failed to load conversation',
        );
        return false;
      } finally {
        if (latestRequestedIdRef.current === conversationId) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const createConversation = useCallback(async (title?: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await agentApi.createConversation(title);
      const mapped = mapConversationDetail(data);
      setConversations((prev) => [mapConversation(data), ...prev]);
      setActiveConversation(mapped);
      return mapConversation(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create conversation',
      );
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      setLoading(true);
      setError(null);

      try {
        await agentApi.deleteConversation(conversationId);
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));

        // Clear active if deleted
        if (activeConversation?.id === conversationId) {
          setActiveConversation(null);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to delete conversation',
        );
      } finally {
        setLoading(false);
      }
    },
    [activeConversation],
  );

  const updateActiveConversation = useCallback(
    (updater: (prev: ConversationDetail) => ConversationDetail) => {
      setActiveConversation((prev) => (prev ? updater(prev) : null));
    },
    [],
  );

  return {
    conversations,
    activeConversation,
    loading,
    error,
    fetchConversations,
    loadConversation,
    createConversation,
    deleteConversation,
    updateActiveConversation,
    setActiveConversation,
  };
}
